import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useStore, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import {
  fetchPodcasts,
  resetPodcasts,
  setCurrentPodcast,
  setSelectedGenre,
  resetPodcastsByGenre,
  fetchTopPodcastsForDemo,
  resetDemoModePodcasts,
  fetchCachedPodcast,
  setShouldFetchTopPodcasts,
  resetLoadingsOnDemoStop,
  setActivePlatform,
} from '../redux/slices/podcastsSlice';
import { getFavorites } from '../utils/favorites';
import { normalizeGenre } from '../utils/normalizeGenre';
import { updatePodcastUrl, isSpotifyId } from '../utils/updatePodcastUrl';
const FAVORITES_THRESHOLD = 20;

export const useAutoDemo = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const location = useLocation();
  const isFirstLoadRef = useRef(true);
  const demoTimeoutRef = useRef(null);
  const isUserInteractedRef = useRef(false);
  const isDemoAllowedRef = useRef(true);
  const currentPodcastIndexRef = useRef(0);
  const cachedPodcastDataUsed = useRef(false);
  const needNewBatchRef = useRef(true);
  const { isOnlyFavoritesForDemo } = useSelector((state) => state.podcasts);
  const isCleanUrl = location.search === '';

  const canStartDemo = useCallback(() => {
    const conditions = {
      isFirstLoad: isFirstLoadRef.current,
      isNotInteracted: !isUserInteractedRef.current,
      isDemoAllowed: isDemoAllowedRef.current,
      isCleanUrl: isCleanUrl,
    };

    return Object.values(conditions).every(Boolean);
  }, [isCleanUrl]);

  const getPodcastsState = () => store.getState().podcasts;

  const loadDemoPodcastsAndStart = useCallback(async () => {
    if (!canStartDemo()) return;

    const favorites = getFavorites();
    const cacheUsedOrHasFavorites = cachedPodcastDataUsed.current || favorites.length > 0;

    const handleDemoRestart = () => {
      if (canStartDemo()) {
        loadDemoPodcastsAndStart();
      }
    };

    const handleError = (error) => {
      console.error('Demo mode error:', error);
      demoTimeoutRef.current = setTimeout(handleDemoRestart, 4000);
    };

    const startNextDemoCycle = () => {
      dispatch(setSelectedGenre(null));
      dispatch(resetPodcastsByGenre());
      demoTimeoutRef.current = setTimeout(handleDemoRestart, 100);
    };

    const waitForPodcastsCarousel = (correctGenre) => {
      const checkPodcastsCarousel = () => {
        if (getPodcastsState().podcastsByGenre[correctGenre]?.length > 0) {
          demoTimeoutRef.current = setTimeout(() => {
            if (!canStartDemo()) return;
            startNextDemoCycle();
          }, 10000);
        } else {
          demoTimeoutRef.current = setTimeout(checkPodcastsCarousel, 100);
        }
      };
      checkPodcastsCarousel();
    };

    const handlePodcastGenre = (currentPodcast) => {
      const isSpotify = isSpotifyId(currentPodcast.collectionId);
      let correctGenre;

      if (isSpotify) {
        const availableGenre = getPodcastsState().spotifyDemoPodcastsAviableGenre;

        if (!getPodcastsState().podcastsByGenre[availableGenre].length) {
          startNextDemoCycle();
          return;
        }

        correctGenre = availableGenre;
      } else {
        const firstAvailableGenre = currentPodcast.genres.find((genre) =>
          getPodcastsState().genres.some((g) => g.name === normalizeGenre(genre))
        );

        if (!firstAvailableGenre) {
          startNextDemoCycle();
          return;
        }

        correctGenre = normalizeGenre(firstAvailableGenre);
      }

      const waitForPodcastsData = () => {
        if (getPodcastsState().podcastsByGenre[correctGenre]?.length > 0) {
          dispatch(setSelectedGenre(correctGenre));
          waitForPodcastsCarousel(correctGenre);
        } else {
          demoTimeoutRef.current = setTimeout(waitForPodcastsData, 100);
        }
      };

      waitForPodcastsData();
    };

    const checkAndUpdateCurrentPodcastIndex = () => {
      if (currentPodcastIndexRef.current >= getPodcastsState().demoModePodcasts.length) {
        cachedPodcastDataUsed.current = true;
        needNewBatchRef.current = true;
      }
    };

    const handlePodcastFound = (foundPodcast) => {
      if (!foundPodcast || !canStartDemo()) return;

      if (cacheUsedOrHasFavorites) {
        dispatch(setCurrentPodcast(foundPodcast));
      }
      demoTimeoutRef.current = setTimeout(() => {
        if (!canStartDemo()) return;
        handlePodcastGenre(foundPodcast);
      }, 9000);
    };

    const isOnlyFavoritesForDemo = getPodcastsState().isOnlyFavoritesForDemo;
    const hasSufficientFavorites = favorites.length >= FAVORITES_THRESHOLD;
    const batchSize = hasSufficientFavorites ? favorites.length : FAVORITES_THRESHOLD;

    if (needNewBatchRef.current && cacheUsedOrHasFavorites) {
      dispatch(setShouldFetchTopPodcasts(true));
      try {
        let response = await dispatch(
          fetchTopPodcastsForDemo({
            startDate: moment().startOf('day').format(),
            country: 'United States',
            genre: 'Top Podcasts',
            batchSize,
            favorites: favorites.map((fav) => fav.podcastId).join(','),
            onlyFavorites: isOnlyFavoritesForDemo,
          })
        ).unwrap();

        if (!response.topPodcasts?.length) {
          await dispatch(
            fetchTopPodcastsForDemo({
              startDate: moment().subtract(1, 'days').startOf('day').format(),
              country: 'United States',
              genre: 'Top Podcasts',
              batchSize,
              favorites: favorites.map((fav) => fav.podcastId).join(','),
              onlyFavorites: isOnlyFavoritesForDemo,
            })
          ).unwrap();
        }

        if (!canStartDemo()) return;

        currentPodcastIndexRef.current = 0;
        needNewBatchRef.current = false;
      } catch (error) {
        handleError(error);
        return;
      }
    } else if (!cachedPodcastDataUsed.current) {
      try {
        await dispatch(fetchCachedPodcast()).unwrap();
      } catch (error) {
        handleError(error);
      }
    }

    const currentPodcast = getPodcastsState().demoModePodcasts[currentPodcastIndexRef.current];

    try {
      if (cacheUsedOrHasFavorites) {
        const isSpotify = isSpotifyId(currentPodcast.podcastId);
        dispatch(setActivePlatform(isSpotify ? 'spotify' : 'apple'));
        const response = await dispatch(fetchPodcasts({ name: currentPodcast.podcastId, isId: true })).unwrap();

        const foundPodcast = response.find(
          (item) => item.collectionName === currentPodcast.name && item.artistName === currentPodcast.artist
        );

        if (!foundPodcast) {
          currentPodcastIndexRef.current++;
          checkAndUpdateCurrentPodcastIndex();

          demoTimeoutRef.current = setTimeout(() => {
            if (canStartDemo()) {
              loadDemoPodcastsAndStart();
            }
          }, 100);
          return;
        }

        handlePodcastFound(foundPodcast);

        cachedPodcastDataUsed.current = true;
      } else {
        handlePodcastFound(getPodcastsState().currentPodcast);
      }

      currentPodcastIndexRef.current++;
      checkAndUpdateCurrentPodcastIndex();
    } catch (error) {
      handleError(error);
    }
  }, [store, dispatch, canStartDemo]);

  useEffect(() => {
    if (isOnlyFavoritesForDemo) {
      isFirstLoadRef.current = true;
      isUserInteractedRef.current = false;
      isDemoAllowedRef.current = true;
      needNewBatchRef.current = true;
      currentPodcastIndexRef.current = 0;

      attachEventListeners();

      loadDemoPodcastsAndStart();
    } else {
      if (demoTimeoutRef.current) {
        clearTimeout(demoTimeoutRef.current);
      }
      removeEventListeners();
    }
  }, [isOnlyFavoritesForDemo]);

  const attachEventListeners = () => {
    const events = ['mousedown', 'wheel', 'touchstart'];
    events.forEach((event) => {
      document.addEventListener(event, handleUserInteraction, { capture: true });
    });
    document.addEventListener('keydown', handleKeyDown, { capture: true });
  };

  const removeEventListeners = () => {
    const events = ['mousedown', 'wheel', 'touchstart'];
    events.forEach((event) => {
      document.removeEventListener(event, handleUserInteraction, { capture: true });
    });
    document.removeEventListener('keydown', handleKeyDown, { capture: true });
  };

  const handleUserInteraction = () => {
    if (isFirstLoadRef.current) {
      isUserInteractedRef.current = true;
      isFirstLoadRef.current = false;
      isDemoAllowedRef.current = false;
      cachedPodcastDataUsed.current = false;
      getPodcastsState().currentPodcast && updatePodcastUrl(getPodcastsState().currentPodcast.collectionId);
      dispatch(resetDemoModePodcasts());
      dispatch(resetLoadingsOnDemoStop());

      if (demoTimeoutRef.current) {
        clearTimeout(demoTimeoutRef.current);
      }

      removeEventListeners();
    }
  };

  const handleKeyDown = (event) => {
    const serviceKeys = ['Alt', 'Control', 'Shift', 'Meta', 'CapsLock', 'Tab'];
    if (serviceKeys.includes(event.key)) {
      return;
    }
    handleUserInteraction();
  };

  useEffect(() => {
    if (!isCleanUrl) {
      isDemoAllowedRef.current = false;
      return;
    }

    if (canStartDemo()) {
      dispatch(setSelectedGenre(null));
      dispatch(resetPodcasts());
      loadDemoPodcastsAndStart();

      attachEventListeners();
    }
  }, [isCleanUrl, loadDemoPodcastsAndStart, dispatch, canStartDemo]);
};
